import { useEffect } from 'react';
import { Link } from 'react-router';

import { Logo } from '@/components/brand/Logo';
import { McpTerminal } from '@/components/landing/McpTerminal';
import { SampleTimeLogs } from '@/components/landing/SampleTimeLogs';
import { SiteFooter } from '@/components/landing/SiteFooter';
import {
  Eyebrow,
  Metric,
  PageContainer,
  PriceCard,
  ReportCard,
  Section,
  SectionHeading,
  SectionIntro,
  Step,
} from '@/components/landing/sections';
import { buttonVariants } from '@/components/ui/button';
import { useSignupOpen } from '@/lib/signup';
import { cn } from '@/lib/utils';

/**
 * ランディングページ。
 *
 * 正は docs/design/INOU_Base_Design.dc.html の `1a LP`。
 *
 * ログイン済みかどうかで導線だけを差し替える。ログイン済みでも中身は見せる
 * (ここを閉じると、URL を共有された人が中身を確認できない)。
 *
 * **数字は実装で裏が取れるものだけを書く。** ヒーローの 3 つは順に
 * 「記録経路が MCP だけであること」「接続に要るのが URL と PAT だけであること」
 * 「apps/api/src/signup.ts の TRIAL_DAYS = 14 かつ決済導線が無いこと」に対応する。
 * ダッシュボードの見本として出す数値には SAMPLE と明示する。
 */
export function LandingPage({ signedIn }: { signedIn: boolean }) {
  const signupOpen = useSignupOpen();

  /*
   * 節へのリンク (`/#pricing`) で他のページから来たときに、その節へ送る。
   *
   * フッターの節リンクは素のアンカーなので実ドキュメント遷移になるが、
   * 着地の時点では `/api/auth/me` の応答待ちで「読み込み中…」しか
   * 出ておらず、ブラウザは対象を見つけられないまま諦める。節を持って
   * いるのはこの画面なので、ここが描画された時点で自分で送る。
   */
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  /*
   * サインアップを閉じている間は、押しても着地できない導線を出さない。
   * LP 自体は閉じない (製品の説明は読めるままにする)。
   */
  const preparing = (
    <span className="block border border-line-strong bg-surface px-4 py-3 text-center text-sm text-ink-muted sm:inline sm:px-6 sm:py-3.5 sm:text-left">
      {'新規のお申し込みは準備中です'}
    </span>
  );

  /*
   * 中への導線。**寸法は置き場所で違う**ので、要素を 1 つ作り回すのではなく
   * 呼ぶたびに組む。ヘッダーは狭い幅でロゴとログインと同じ行に収める必要があり、
   * 本文側は逆に指で押せる幅いっぱいのボタンにしたい。640px 以上では
   * どちらも同じ (= 従来の) 寸法に戻るので、デスクトップでは差が出ない。
   */
  const enterSize = signedIn ? 'sm:px-8 sm:py-4 sm:text-base' : 'sm:px-7 sm:py-3.5';
  const enterLink = (mobile: string) =>
    signedIn ? (
      <Link
        to="/utilization"
        className={cn(buttonVariants({ variant: 'primary' }), mobile, enterSize)}
      >
        ダッシュボードへ
      </Link>
    ) : signupOpen ? (
      <Link to="/signup" className={cn(buttonVariants({ variant: 'primary' }), mobile, enterSize)}>
        14日間無料で試す
      </Link>
    ) : (
      preparing
    );

  /** 本文に置く導線。狭い幅では横幅いっぱいにして、指で押せる高さを確保する。 */
  const enter = enterLink('w-full justify-center px-4 py-3 sm:w-auto');

  return (
    <div className="min-h-screen bg-surface text-ink">
      <header className="border-b border-line bg-surface-raised">
        <PageContainer className="flex items-center justify-between gap-3 py-3.5 sm:gap-3 sm:py-5">
          {/*
            ロゴは狭い幅で 36px に落とす。48px のままだと、ログインと申し込みの
            2 つと同じ行に並べたときに行が折れてヘッダーが 2 段になる。
          */}
          <Logo height={48} className="h-9 w-auto text-brand-500 sm:h-12" />
          <nav className="flex items-center gap-1 sm:gap-3">
            {signedIn ? null : (
              <Link
                to="/login"
                className={cn(
                  buttonVariants({ variant: 'ghost' }),
                  'px-2 text-sm text-ink-muted sm:px-3 sm:text-base',
                )}
              >
                ログイン
              </Link>
            )}
            {/* 閉じている間、ヘッダーには準備中の断りを置かない (ログインだけ残す) */}
            {signedIn || signupOpen ? enterLink('px-3 py-2.5') : null}
          </nav>
        </PageContainer>
      </header>

      <PageContainer className="grid items-center gap-10 pt-12 pb-14 sm:gap-12 sm:pt-20 sm:pb-20 md:gap-14 md:pt-28 md:pb-24 lg:grid-cols-[1.18fr_0.82fr]">
        <div className="flex flex-col gap-5 sm:gap-7">
          <Eyebrow>ENGINEERING TIME WITHOUT THE FORM</Eyebrow>
          {/*
            <br /> で 2 行に割っているので、字が入り切らない幅では
            3 行目に数文字だけ残ってしまう。幅に応じて段階を付ける。

            日本語は 1 文字 = 1em なので、入り切る字送りは幅から逆算できる。
            長い方の行が 13 文字なので、本文幅 335px (375px − 左右 20px) では
            25px が上限になる。**入るはずのない大きさを置かない**——溢れた分は
            折り返しではなく「3 行目に『た。』だけ」という崩れ方をする。

            手のひらの幅は 320〜430px と幅があり、段を足しても境目のすぐ手前で
            同じ崩れ方が戻ってくる。**ここだけは段ではなく幅そのものに追従させる**
            (6.7vw ≒ 13 文字 + 左右の余白)。640px 以上は段に戻すので、
            この式が効くのはモバイルの帯だけ。
          */}
          <h1 className="text-[clamp(20px,6.7vw,25px)] leading-[1.2] font-bold tracking-tight sm:text-[36px] sm:leading-[1.15] md:text-[44px] lg:text-[56px]">
            エンジニアに工数入力を
            <br />
            強いる時代は終わりました。
          </h1>
          <p className="max-w-2xl text-[15px] leading-relaxed text-ink-muted sm:text-[17px]">
            {
              'INOU は、エンジニアが普段使っている AI クライアントに MCP で接続するだけで動きます。会話の内容から作業とプロジェクトを読み取って工数として記録するため、入力フォームに向かう時間は必要ありません。管理者はその記録をもとに、予実・稼働・資産化までダッシュボードで確認できます。'
            }
          </p>

          {/* 狭い幅では 2 つのボタンを縦に積む (横並びだと折り返しで幅が半端になる) */}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            {enter}
            <a
              href="#how-it-works"
              className={cn(
                buttonVariants({ variant: 'ghost' }),
                'w-full justify-center border-line-strong px-4 py-3 text-sm text-ink-muted sm:w-auto sm:border-transparent sm:px-6 sm:py-4 sm:text-base',
              )}
            >
              MCP の接続手順を見る
            </a>
          </div>

          <div className="mt-1 grid gap-4 border-t border-line pt-6 sm:mt-4 sm:gap-8 sm:pt-7 sm:grid-cols-3">
            <Metric value="0" label="エンジニアが開く画面の数" />
            <Metric value="2分" label="MCP 接続から記録開始まで" />
            <Metric value="14日" label="無料トライアル / カード登録不要" />
          </div>
        </div>

        <McpTerminal />
      </PageContainer>

      <Section id="how-it-works">
        <SectionIntro>
          <Eyebrow>HOW IT WORKS</Eyebrow>
          <SectionHeading>仕組みは2つだけです</SectionHeading>
        </SectionIntro>
        <div className="grid gap-4 sm:gap-8 lg:grid-cols-2">
          <Step
            index="01"
            persona="ENGINEER"
            title="現場は MCP で AI と話すだけ"
            body="使っている AI クライアントに INOU の MCP サーバーを登録します。設定するのは接続 URL と個人アクセストークンの2つだけ。以降は普段どおり会話するだけで、作業内容とプロジェクトが推定され工数として残ります。"
          >
            <div className="flex items-center gap-3 border-t border-line pt-5 font-mono text-xs tracking-wider text-ink-faint">
              <span>AI クライアント</span>
              <span aria-hidden>→</span>
              <span>INOU MCP</span>
              <span aria-hidden>→</span>
              <span className="text-brand-700">time log</span>
            </div>
          </Step>
          <Step
            index="02"
            persona="MANAGER"
            title="管理者はダッシュボードで予実管理"
            body="アサイン予定と実績の差分、プロジェクトごとの資産化・費用化の比率が自動で集計されます。月次の報告資料をつくり直す必要はありません。"
          >
            <div className="flex flex-col gap-3 border-t border-line pt-5">
              {/* 狭い幅では字送りと字間を落とす。1 行に収まらないと末尾に「ん」だけが残る */}
              <span className="font-mono text-[11px] tracking-[0.08em] text-ink-faint sm:text-xs sm:tracking-[0.14em]">
                SAMPLE — 表示例であり実測値ではありません
              </span>
              {/* 3 つ横並びだと手のひらの幅では数値どうしがぶつかる (112% と 61%) */}
              <div className="grid gap-4 sm:grid-cols-3">
                <Metric value="78%" label="予実進捗" />
                <Metric value="112%" label="稼働超過" />
                <Metric value="61%" label="資産化比率" />
              </div>
            </div>
          </Step>
        </div>
      </Section>

      <Section id="dashboard">
        <SectionIntro>
          <Eyebrow>DASHBOARD</Eyebrow>
          <SectionHeading>記録は、こう積み上がります</SectionHeading>
          <span className="max-w-3xl text-[15px] leading-relaxed text-ink-muted sm:text-base">
            {
              'MCP から入った記録は、そのまま管理画面の工数明細に積み上がります。下に出ているのは実際の画面と同じ部品で組んだ表示例です。人単位とプロジェクト単位の切り替えも実物と同じように動きます。メンバー名も数値も見本で、実在の組織のデータではありません。'
            }
          </span>
        </SectionIntro>
        <SampleTimeLogs />
      </Section>

      <Section id="reports">
        <SectionIntro>
          <Eyebrow>REPORTS</Eyebrow>
          <SectionHeading>集計できること</SectionHeading>
          <span className="max-w-3xl text-[15px] leading-relaxed text-ink-muted sm:text-base">
            {
              '記録された工数は、月ごとに2種類のレポートへ集計されます。どちらも画面で見るほかに CSV で書き出せます。'
            }
          </span>
        </SectionIntro>

        <div className="grid gap-4 sm:gap-8 lg:grid-cols-2">
          <ReportCard
            name="稼働レポート"
            axis="メンバー × 月"
            summary="立てたアサイン計画に対して実際にどれだけ動いたか、予定と実績の差をメンバー単位で突き合わせます。"
            columns={[
              { label: '予定 / 実績', detail: 'アサインした時間と、MCP から記録された実績時間' },
              { label: '消化率', detail: '実績 ÷ 予定。超過しているメンバーには印が付きます' },
              {
                label: '直接作業',
                detail: '受託・自社プロダクトの開発に当たった時間',
              },
              {
                label: '間接作業',
                detail: '保守・会議・その他、および社内業務プロジェクトの時間',
              },
              { label: '請求対象時間', detail: '請求可能なプロジェクトに当たった時間' },
            ]}
            note="CSV で書き出せます。MCP Connect プランの人には自分の行だけが返ります。"
          />
          <ReportCard
            name="資産化・収支レポート"
            axis="プロジェクト × 月"
            summary="ソフトウェアとして資産計上できる時間と費用処理する時間を分け、そのまま原価・売上・粗利まで出します。"
            columns={[
              {
                label: '資産化 / 費用化',
                detail: '資産計上できるプロジェクトの開発時間と、それ以外の時間',
              },
              { label: '資産化率', detail: '資産化時間 ÷ 実績時間' },
              { label: '原価', detail: '時間 × 原価単価。資産化分と費用化分に分けて出ます' },
              { label: '売上', detail: '時間 × 売上単価' },
              { label: '粗利', detail: '売上 − 原価' },
            ]}
            note="CSV で書き出せます。プロジェクトの資産化可否を後から変えても、締めた月の数字は動きません。"
          />
        </div>

        <div className="mt-6 grid gap-6 border-t border-line pt-6 sm:mt-8 sm:pt-8 lg:grid-cols-2">
          <div className="flex flex-col gap-2">
            <span className="font-mono text-xs tracking-[0.14em] text-ink-subtle">GRANULARITY</span>
            <p className="text-[15px] leading-relaxed text-ink-muted sm:text-base">
              {
                '管理する粒度は「誰が・どのプロジェクトに・どの月に・何時間」までです。タスクやチケット単位の管理は行いません。現場に入力の手間を戻さないためです。'
              }
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="font-mono text-xs tracking-[0.14em] text-ink-subtle">RATES</span>
            <p className="text-[15px] leading-relaxed text-ink-muted sm:text-base">
              {
                '単価はアサインごとの個別単価、メンバーの単価、組織の既定単価の順に解決されます。全員分を登録しなくても、既定単価だけで金額の集計は動きます。'
              }
            </p>
          </div>
        </div>
      </Section>

      <Section id="pricing">
        <SectionIntro>
          <Eyebrow>PRICING</Eyebrow>
          <SectionHeading>料金プラン</SectionHeading>
          <span className="max-w-3xl text-[15px] leading-relaxed text-ink-muted sm:text-base">
            <strong className="font-bold text-ink">シート単位の課金です。</strong>
            {
              '利用する人 1 名につき 1 シートを使い、どちらのシートになるかはその人のロールで決まります。2 つのプランは併用できます。'
            }
          </span>
        </SectionIntro>

        <div className="grid gap-4 sm:gap-8 lg:grid-cols-2">
          <PriceCard
            name="MCP Connect"
            audience="現場エンジニア向け"
            price="$3"
            unit="/ 1シート・月"
            seatNote="ROLE: mcp"
            summary="UI レスのプランです。AI クライアントに接続するだけで工数が記録されます。"
            features={[
              'MCP サーバー接続（無制限）',
              '会話からの工数自動記録',
              'プロジェクトの自動推定',
              '自分の稼働サマリー（他のメンバーの数字は見えません）',
            ]}
            action={
              <a
                href="#how-it-works"
                className={cn(
                  buttonVariants({ variant: 'ghost' }),
                  'px-0 text-base text-ink-muted',
                )}
              >
                接続手順を見る
              </a>
            }
          />
          <PriceCard
            name="Full Management"
            audience="管理者向け"
            price="$10"
            unit="/ 1シート・月"
            seatNote="ROLE: member / admin"
            summary="全機能入りのダッシュボードが使えます。予実管理と会計レポートまで対応します。"
            features={[
              'MCP Connect のすべての機能',
              'アサイン・稼働管理ダッシュボード',
              '資産化・収支レポート',
              'レポートの CSV 書き出し',
              'メンバー・単価・プロジェクトの管理（admin）',
            ]}
            recommended
            action={enter}
          />
        </div>

        <div className="mt-6 grid gap-8 border-t border-line pt-6 sm:mt-8 sm:pt-8 lg:grid-cols-2">
          <div className="flex flex-col gap-3">
            <span className="font-mono text-xs tracking-[0.14em] text-ink-subtle">EXAMPLE</span>
            <p className="text-[15px] leading-relaxed text-ink-muted sm:text-base">
              エンジニア 10 人を MCP Connect、管理者 2 人を Full Management で使う場合:
            </p>
            <p className="font-mono text-base text-brand-700">
              $3 × 10 + $10 × 2 = <span className="text-[28px]">$50</span> / 月
            </p>
            <p className="text-sm leading-relaxed text-ink-subtle">
              {'シートは招待した時点で消費します。無効化したメンバーはシートを消費しません。'}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <span className="font-mono text-xs tracking-[0.14em] text-ink-subtle">TRIAL</span>
            <p className="text-[15px] leading-relaxed text-ink-muted sm:text-base">
              {signupOpen
                ? 'サインアップすると 14 日間、Full Management 5 シートと MCP Connect 5 シートを無料で試せます。クレジットカードの登録は要りません。'
                : '新規のお申し込みは準備中です。受付を開始したら、14 日間 Full Management 5 シートと MCP Connect 5 シートを無料で試せます。クレジットカードの登録は要りません。'}
            </p>
            <p className="text-sm leading-relaxed text-ink-subtle">
              {
                '期間が過ぎても記録した内容は読めます。閲覧と CSV 書き出しは残り、新しく記録・編集する操作だけが止まります。'
              }
            </p>
          </div>
        </div>
      </Section>

      <SiteFooter signedIn={signedIn} />
    </div>
  );
}
